/**
 * Varejo · Valuation (réplica do Estudo MTM #4, PR2).
 *
 * Consome GET /api/varejo/valuation (snapshot ESTÁTICO — sem polling, 1 fetch
 * no mount). Toggle Nominal (off / cenário A) ↔ Indexado (on / IPCA) é puro
 * client-side: os dois cenários vêm no mesmo payload. TODOS os números saem do
 * endpoint — nada hardcoded (segue docs/valuation_varejo_mock.html).
 *
 * SENSÍVEL: a tabela drill mostra razao (nome cliente/órgão). É tela autenticada
 * interna (OK renderizar), mas NÃO logar razao no console/erro.
 *
 * Empate por vértice (addendum): a Mesa pensa por cal27/cal28/… — o δ paralelo
 * achatado (breakeven) esconde ONDE mora o buraco. A tabela por-vértice é a
 * leitura Mesa-native; o headline paralelo (δ/nível) fica no card de Valuation.
 */
import { useState } from "react";
import { CheckCircle2, FlaskConical } from "lucide-react";
import { Loading } from "../Loading";
import { useVarejoValuation } from "../../hooks/useVarejo";
import {
  fmtBRLcompact,
  fmtBRLcompactSigned,
  fmtMWm,
} from "../../utils/formatters";
import type {
  ValuationEscopo,
  ValuationVarejoResponse,
} from "../../api/types";

type Modo = "off" | "on";
type EscopoKey = "Total" | "EG" | "PF";

function fmtMesAno(iso: string): string {
  const [y, m] = iso.split("-");
  return `${m}/${y}`;
}

/** "~210 mil MWh" — magnitude da energia anual/escopo (inteiro, milhar pt-BR). */
function fmtMilMwh(mwh: number): string {
  return `~${Math.round(mwh / 1000).toLocaleString("pt-BR")} mil MWh`;
}

/** R$/MWh inteiro pt-BR. null → "—". */
function fmtRsMwh(v: number | null | undefined, dec = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return `R$ ${v.toLocaleString("pt-BR", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  })}`;
}

const sgn = (v: number | null) => (v !== null && v < 0 ? "text-indra-negativo" : "text-indra-positivo");

export function ValuationTab() {
  const { data, isLoading, error } = useVarejoValuation();
  const [modo, setModo] = useState<Modo>("off");
  const [verticeEsc, setVerticeEsc] = useState<EscopoKey>("Total");

  if (isLoading && !data) return <Loading label="Carregando Valuation…" />;
  if (error)
    return (
      <div className="text-sm text-indra-negativo">
        Falha ao carregar o Valuation — tente Atualizar.
      </div>
    );
  if (!data) return null;

  // Snapshot vazio (sem ingestão) → empty state honesto.
  if (data.metadados.n_linhas === 0) {
    return (
      <div
        className="rounded-xl border border-white/10 bg-indra-azul-surface p-8 max-w-xl"
        data-testid="valuation-empty"
      >
        <div className="flex items-center gap-3 text-white/70">
          <FlaskConical size={20} className="text-indra-turquesa" />
          <span className="text-sm font-bold uppercase tracking-wider">
            Valuation da carteira
          </span>
        </div>
        <p className="text-sm text-white/50 mt-3">
          Sem snapshot do Estudo MTM ingerido ainda. O valuation aparece aqui
          quando a carteira do Estudo for carregada.
        </p>
      </div>
    );
  }

  const md = data.metadados;
  const isIdx = modo === "on";
  const ativo = isIdx ? data.on : data.off;
  const tot = ativo["Total"];
  const eg = ativo["EG"];
  const pf = ativo["PF"];
  // VP do cenário oposto (hint secundário no card).
  const vpOposto = (isIdx ? data.off : data.on)["Total"].vp;
  const labels = md.labels_modalidade;

  return (
    <div className="space-y-6" data-testid="valuation-tab">
      {/* ---- header ---- */}
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <h1 className="text-lg font-semibold text-white">
            Valuation da carteira — Varejo
          </h1>
          <p className="text-[13px] text-white/50 mt-1">
            Réplica do{" "}
            <span className="text-indra-turquesa font-medium">Estudo MTM</span> ·
            Data Carteira {fmtMesAno(data.data_carteira)} · marcado a mercado ·{" "}
            {md.n_contratos} contratos ({md.n_contratos_eg} EG / {md.n_contratos_pf} PF)
          </p>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-indra-positivo bg-indra-positivo/10 border border-indra-positivo/25 rounded-full px-2.5 py-0.5 mt-2">
            <CheckCircle2 size={12} /> reconcilia o Estudo ao centavo (Compra exata · VP Δ pequeno)
          </span>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex bg-indra-azul-surface border border-white/10 rounded-lg p-0.5" data-testid="valuation-toggle">
            {(["off", "on"] as Modo[]).map((m) => (
              <button
                key={m}
                onClick={() => setModo(m)}
                className={`px-3.5 py-1.5 rounded-md text-[13px] ${
                  modo === m
                    ? "bg-indra-turquesa-muted text-indra-turquesa font-medium"
                    : "text-white/50 hover:text-white/80"
                }`}
              >
                {m === "off" ? "Nominal" : "Indexado (IPCA)"}
              </button>
            ))}
          </div>
          <div className="font-mono text-[12px] text-white/50 border border-white/10 rounded-lg px-2.5 py-1.5">
            Data Carteira · {fmtMesAno(data.data_carteira)}
            {md.curva_asof && (
              <span className="text-white/30"> · curva {md.curva_asof}</span>
            )}
          </div>
        </div>
      </div>

      {/* ---- cards ---- */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {/* Valuation (VP) — span 2 */}
        <div className="md:col-span-2 rounded-xl border border-white/10 bg-indra-azul-surface p-4">
          <div className="text-[12px] uppercase tracking-wide text-white/50">
            Valuation (VP margem líq.)
          </div>
          <div className={`font-mono text-2xl font-semibold mt-2 ${sgn(tot.vp)}`}>
            {fmtBRLcompactSigned(tot.vp)}
            {vpOposto !== null && (
              <span className="text-[13px] text-white/40 font-normal">
                {" "}· {isIdx ? "nominal" : "indexado"} {fmtBRLcompactSigned(vpOposto)}
              </span>
            )}
          </div>
          {tot.vp !== null && tot.vp < 0 && tot.breakeven && (
            <div className="mt-2.5 pt-2 border-t border-dashed border-white/10 text-[12px] text-amber-300">
              ↘ empata: compra/forward a{" "}
              <b className="font-mono font-semibold">
                {fmtRsMwh(tot.breakeven.preco_compra_empate_rs_mwh)}/MWh
              </b>{" "}
              <span className="text-white/35">
                (−{fmtRsMwh(tot.breakeven.delta_spread_rs_mwh)}/MWh no spread)
              </span>
            </div>
          )}
          {tot.vp === null && (
            <div className="text-[11px] text-white/35 mt-1">
              sem curva forward as-of — VP indisponível
            </div>
          )}
        </div>

        {/* Margem bruta */}
        <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-4">
          <div className="text-[12px] uppercase tracking-wide text-white/50">
            Margem bruta
          </div>
          <div className={`font-mono text-2xl font-semibold mt-2 ${sgn(tot.mb)}`}>
            {fmtBRLcompactSigned(tot.mb)}
          </div>
          <div className="text-[11px] text-white/35 mt-1">
            Venda {fmtBRLcompact(tot.venda)} − Compra {fmtBRLcompact(tot.compra)}
          </div>
        </div>

        {/* Efeito IPCA isolado */}
        <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-4">
          <div className="text-[12px] uppercase tracking-wide text-white/50">
            Efeito IPCA (isolado)
          </div>
          <div className={`font-mono text-2xl font-semibold mt-2 ${sgn(md.vp_delta_ipca_isolamento)}`}>
            {fmtBRLcompactSigned(md.vp_delta_ipca_isolamento)}
          </div>
          <div className="text-[11px] text-white/35 mt-1">
            só-toggle · sinal sólido
            {md.ipca_on_exato_provisorio && " (exato provisório)"}
          </div>
        </div>
      </div>

      {/* ---- cadeia do valor ---- */}
      <ValueChain tot={tot} />

      {/* ---- split por modalidade ---- */}
      <section>
        <h2 className="text-[13px] uppercase tracking-wide text-white/50 mb-3">
          Por modalidade
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <ModalidadePanel sigla="EG" label={labels.EG} esc={eg} />
          <ModalidadePanel sigla="PF" label={labels.PF} esc={pf} />
        </div>
      </section>

      {/* ---- empate por vértice (Mesa-native) ---- */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[13px] uppercase tracking-wide text-white/50">
            Empate por vértice <span className="normal-case text-white/30">(leitura por ano de entrega)</span>
          </h2>
          <div className="flex gap-1" data-testid="valuation-vertice-escopo">
            {(["Total", "EG", "PF"] as EscopoKey[]).map((e) => (
              <button
                key={e}
                onClick={() => setVerticeEsc(e)}
                className={`px-2.5 py-1 rounded-lg text-[11px] border ${
                  verticeEsc === e
                    ? "border-indra-turquesa/60 bg-indra-turquesa-muted text-indra-turquesa"
                    : "border-white/10 text-white/50 hover:bg-white/5"
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>
        <VerticeTable esc={ativo[verticeEsc]} />
        <p className="text-[11px] text-white/30 mt-2">
          δ por vértice = o movimento que zera a contribuição VP daquele ano. Anos não-negativos
          mostram "—" (sem buraco). O headline paralelo (−{fmtRsMwh(tot.breakeven?.delta_spread_rs_mwh ?? null)}/MWh)
          fica no card de Valuation; aqui é onde o buraco aparece, vértice a vértice.
        </p>
      </section>

      {/* ---- drill por contrato ---- */}
      <section>
        <h2 className="text-[13px] uppercase tracking-wide text-white/50 mb-3">
          Contratos — maiores impactos no VP
        </h2>
        <ContratoTable data={data} isIdx={isIdx} />
      </section>

      {/* ---- crítica / premissas ---- */}
      <section>
        <div className="rounded-xl border border-indra-negativo/25 bg-indra-negativo/5 p-4">
          <h3 className="text-[13px] uppercase tracking-wide text-indra-negativo/90 mb-2.5">
            Premissas e leitura crítica (réplica fiel do Estudo)
          </h3>
          <ul className="list-disc pl-5 text-[13px] text-white/55 space-y-1.5">
            {md.premissas_congeladas.map((p) => (
              <li key={p}>{p}</li>
            ))}
            <li>
              <b className="text-white/80">Fonte:</b> {md.fonte}
              {md.curva_asof && <> · curva forward as-of {md.curva_asof}</>}.
            </li>
            {isIdx && md.ipca_on_exato_provisorio && (
              <li>
                <b className="text-white/80">IPCA on é provisório:</b> sinal/ordem
                sólidos, valor exato pendente da convenção de aniversário do contrato
                ({md.ipca_on_linhas_reajustadas} linhas reajustadas).
              </li>
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}

/** Cadeia Venda → Compra → MB → custos → ML → VP do escopo Total (modo ativo). */
function ValueChain({ tot }: { tot: ValuationEscopo }) {
  // estática: ml sempre presente (o tipo é nullable pela fonte viva).
  const custos = (tot.ml ?? 0) - tot.mb; // comissão/ICMS/encargos (queda MB→ML)
  const base = Math.max(Math.abs(tot.venda), Math.abs(tot.compra), 1);
  const w = (v: number) => `${Math.min(100, (Math.abs(v) / base) * 100)}%`;

  const Row = ({
    name,
    sub,
    val,
    color,
    total,
  }: {
    name: string;
    sub?: string;
    val: number | null;
    color: string;
    total?: boolean;
  }) => (
    <div className="grid grid-cols-[200px_1fr_130px] items-center gap-3 py-1.5">
      <div className={`text-[13px] ${total ? "font-semibold text-white" : "text-white/80"}`}>
        {name}
        {sub && <span className="block text-[11px] text-white/35">{sub}</span>}
      </div>
      <div className="h-3 rounded">
        <div className={`h-3 rounded ${color}`} style={{ width: val === null ? "0%" : w(val) }} />
      </div>
      <div className={`font-mono text-[13px] text-right ${total ? "font-semibold" : ""} ${val !== null && val < 0 ? "text-indra-negativo" : "text-indra-positivo"}`}>
        {fmtBRLcompactSigned(val)}
      </div>
    </div>
  );

  return (
    <section>
      <h2 className="text-[13px] uppercase tracking-wide text-white/50 mb-3">
        Cadeia do valor — Venda → Valor Presente
      </h2>
      <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-5">
        <Row name="Venda (faturamento contratado)" val={tot.venda} color="bg-indra-turquesa/40" />
        <Row name="Compra" sub="a mercado (curva forward)" val={-Math.abs(tot.compra)} color="bg-indra-negativo/40" />
        <div className="border-t border-dashed border-white/10 my-1.5" />
        <Row name="Margem bruta" val={tot.mb} color="bg-indra-negativo/40" total />
        <Row name="− Comissão / ICMS / encargos" sub="gestão / one-offs embutidos" val={custos} color="bg-indra-negativo/40" />
        <div className="border-t border-dashed border-white/10 my-1.5" />
        <Row name="Margem líquida" val={tot.ml} color="bg-indra-negativo/40" total />
        <Row name="Valor presente" sub="desconto DI, spread 0" val={tot.vp} color="bg-indra-negativo/40" total />
      </div>
    </section>
  );
}

function ModalidadePanel({
  sigla,
  label,
  esc,
}: {
  sigla: "EG" | "PF";
  label: string;
  esc: ValuationEscopo;
}) {
  const pill =
    sigla === "EG"
      ? "bg-indra-turquesa/15 text-indra-turquesa"
      : "bg-amber-400/15 text-amber-300";
  return (
    <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-4">
      <div className="flex justify-between items-baseline">
        <span className="flex items-center gap-2">
          <span className={`text-[10px] px-1.5 py-0.5 rounded ${pill}`}>{sigla}</span>
          <span className="text-white/80 text-sm">{label}</span>
        </span>
        <span className={`font-mono text-lg ${sgn(esc.vp)}`}>
          {fmtBRLcompactSigned(esc.vp)}
        </span>
      </div>
      <div className="flex gap-5 mt-2.5 font-mono text-[13px]">
        <span>
          <span className="font-sans text-white/35 text-[11px]">Volume </span>
          {fmtMWm(esc.vol_mwm)}
        </span>
        <span className="text-indra-turquesa">{fmtMilMwh(esc.vol_mwh)}</span>
      </div>
      <div className="text-[12px] text-white/40 mt-2">
        Venda {fmtBRLcompact(esc.venda)} · MB{" "}
        <span className={sgn(esc.mb)}>{fmtBRLcompactSigned(esc.mb)}</span>
      </div>
      {esc.vp !== null && esc.vp < 0 && esc.breakeven && (
        <div className="text-[11px] text-amber-300/80 mt-1.5">
          empata a {fmtRsMwh(esc.breakeven.preco_compra_empate_rs_mwh)}/MWh
          (−{fmtRsMwh(esc.breakeven.delta_spread_rs_mwh)}/MWh)
        </div>
      )}
    </div>
  );
}

function VerticeTable({ esc }: { esc: ValuationEscopo }) {
  const rows = esc.por_vertice;
  return (
    <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-1 overflow-x-auto">
      <table className="w-full text-[13px]" data-testid="valuation-vertice-tabela">
        <thead>
          <tr className="text-left text-white/45 border-b border-white/10">
            <th className="py-2 px-3">Ano</th>
            <th className="py-2 px-3 text-right">Vol (MWm)</th>
            <th className="py-2 px-3 text-right">Vol (MWh)</th>
            <th className="py-2 px-3 text-right">Fwd atual</th>
            <th className="py-2 px-3 text-right">δ empate</th>
            <th className="py-2 px-3 text-right">Nível de empate</th>
          </tr>
        </thead>
        <tbody className="font-mono">
          {rows.map((v) => (
            <tr key={v.ano} className="border-b border-white/[0.06] hover:bg-white/5">
              <td className="py-1.5 px-3">{v.ano}</td>
              <td className="py-1.5 px-3 text-right">{fmtMWm(v.vol_mwm)}</td>
              <td className="py-1.5 px-3 text-right text-white/60">
                {Math.round(v.vol_mwh).toLocaleString("pt-BR")}
              </td>
              <td className="py-1.5 px-3 text-right">{fmtRsMwh(v.fwd_atual_rs_mwh)}</td>
              <td className={`py-1.5 px-3 text-right ${v.delta_empate_rs_mwh !== null ? "text-amber-300" : "text-white/30"}`}>
                {v.delta_empate_rs_mwh !== null ? `−${fmtRsMwh(v.delta_empate_rs_mwh)}` : "—"}
              </td>
              <td className="py-1.5 px-3 text-right text-white/70">
                {fmtRsMwh(v.nivel_empate_rs_mwh)}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={6} className="py-3 px-3 text-white/40">
                Sem vértices no escopo.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function ContratoTable({
  data,
  isIdx,
}: {
  data: ValuationVarejoResponse;
  isIdx: boolean;
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-1 overflow-x-auto">
      <table className="w-full text-[13px]" data-testid="valuation-contrato-tabela">
        <thead>
          <tr className="text-left text-white/45 border-b border-white/10">
            <th className="py-2 px-3">Contrato / cliente</th>
            <th className="py-2 px-3">Mod.</th>
            <th className="py-2 px-3 text-right">Venda</th>
            <th className="py-2 px-3 text-right">Compra</th>
            <th className="py-2 px-3 text-right">MB</th>
            <th className="py-2 px-3 text-right">ML</th>
            <th className="py-2 px-3 text-right">VP</th>
            {isIdx && <th className="py-2 px-3 text-right">Venda IPCA</th>}
            {isIdx && <th className="py-2 px-3 text-right">VP IPCA</th>}
          </tr>
        </thead>
        <tbody className="font-mono">
          {data.por_contrato.map((c) => (
            <tr key={`${c.id_cliente}-${c.tipo_contrato}`} className="border-b border-white/[0.06] hover:bg-white/5">
              <td className="py-1.5 px-3 font-sans">
                {c.razao ?? "—"}
                <span className="text-white/30 text-[11px] ml-2">{c.id_cliente}</span>
              </td>
              <td className="py-1.5 px-3 font-sans">
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded ${
                    c.tipo_contrato === "EG"
                      ? "bg-indra-turquesa/15 text-indra-turquesa"
                      : "bg-amber-400/15 text-amber-300"
                  }`}
                >
                  {c.tipo_contrato}
                </span>
              </td>
              <td className="py-1.5 px-3 text-right text-white/70">{fmtBRLcompact(c.venda)}</td>
              <td className="py-1.5 px-3 text-right text-white/70">{fmtBRLcompact(c.compra)}</td>
              <td className={`py-1.5 px-3 text-right ${sgn(c.mb)}`}>{fmtBRLcompactSigned(c.mb)}</td>
              <td className={`py-1.5 px-3 text-right ${sgn(c.ml)}`}>{fmtBRLcompactSigned(c.ml)}</td>
              <td className={`py-1.5 px-3 text-right ${sgn(c.vp)}`}>{fmtBRLcompactSigned(c.vp)}</td>
              {isIdx && (
                <td className="py-1.5 px-3 text-right text-white/70">{fmtBRLcompact(c.venda_ipca_on)}</td>
              )}
              {isIdx && (
                <td className={`py-1.5 px-3 text-right ${sgn(c.vp_ipca_on)}`}>
                  {fmtBRLcompactSigned(c.vp_ipca_on)}
                </td>
              )}
            </tr>
          ))}
          {data.por_contrato.length === 0 && (
            <tr>
              <td colSpan={isIdx ? 9 : 7} className="py-3 px-3 text-white/40">
                Sem contratos no snapshot.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
